import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { hotComics, latestUpdates, categories } from '@/mocks/comics';

const allComics = [...hotComics, ...latestUpdates];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4 block"></i>
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-2">分类未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const filteredComics = allComics.filter((c) => c.category === category.name);

  return (
    <>
      <Navbar />
      <main>
        <section className="bg-background-100 pt-24 pb-8">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-4" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700 font-medium">{category.name}漫画</span>
            </nav>

            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
                <i className={`${category.icon} text-xl text-primary-600`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {category.name}漫画
                </h1>
                <p className="text-foreground-500 text-sm mt-0.5">{category.description} · 共 {filteredComics.length} 部作品</p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap" role="tablist" aria-label="漫画分类">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  role="tab"
                  aria-selected={cat.slug === slug}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-white shadow-sm'
                      : 'bg-background-200 text-foreground-600 hover:bg-background-300 hover:text-foreground-900'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-[1440px] mx-auto px-4 md:px-6 py-10">
          <div className="flex items-center gap-3 mb-8 flex-wrap">
            <span className="text-foreground-500 text-sm">排序：</span>
            <button className="px-4 py-1.5 rounded-full bg-primary-500 text-white text-xs font-semibold cursor-pointer whitespace-nowrap">热门推荐</button>
            <button className="px-4 py-1.5 rounded-full bg-background-200 text-foreground-600 text-xs cursor-pointer whitespace-nowrap hover:bg-background-300 transition-colors">最近更新</button>
            <button className="px-4 py-1.5 rounded-full bg-background-200 text-foreground-600 text-xs cursor-pointer whitespace-nowrap hover:bg-background-300 transition-colors">评分最高</button>
          </div>

          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
              {filteredComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.slug}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
                    <img
                      src={comic.coverUrl}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent"></div>
                    <div className="absolute top-2 right-2">
                      <span className="px-2 py-0.5 rounded-full bg-background-50/90 text-foreground-900 text-xs font-semibold whitespace-nowrap">
                        ★ {comic.rating}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium mb-1.5 whitespace-nowrap ${
                        comic.status === '连载中' ? 'bg-primary-500/85 text-white' : 'bg-accent-500/85 text-white'
                      }`}>
                        {comic.status}
                      </span>
                      <h3 className="text-white font-heading font-semibold text-sm md:text-base">{comic.title}</h3>
                    </div>
                  </div>
                  <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2">{comic.description}</p>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
              <p className="text-foreground-500">暂无该分类的漫画，请稍后再来</p>
              <Link to="/category/love" className="inline-block mt-4 text-primary-600 hover:text-primary-700 text-sm cursor-pointer">去恋爱分类看看</Link>
            </div>
          )}
        </section>

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "首页", "item": "http://mostlive.cn/" },
            { "@type": "ListItem", "position": 2, "name": `${category.name}漫画`, "item": `http://mostlive.cn/category/${category.slug}` }
          ]
        }) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": `${category.name}漫画 - 韩漫网`,
          "description": `韩漫网${category.name}漫画分类，收录${category.count}部精选${category.name}韩漫，${category.description}，全部免费在线阅读。`,
          "url": `http://mostlive.cn/category/${category.slug}`
        }) }} />
      </main>
      <Footer />
    </>
  );
}